import React from "react";
import { CheckCircle2Icon, MinusIcon, PlusIcon } from "lucide-react";
import { firm } from "../../data/site";
import { useCaseReviewForm } from "../../hooks/useCaseReviewForm";
import { actionClass } from "../../utils/actionStyles";
import { FormField } from "./FormField";
type CaseReviewFormProps = {
  showReviewNotes: boolean;
};
export function CaseReviewForm({
  showReviewNotes
}: CaseReviewFormProps) {
  const {
    values,
    errors,
    errorList,
    previewSubmitted,
    showOptional,
    setShowOptional,
    summaryRef,
    statusRef,
    handleChange,
    handleSubmit,
    reset
  } = useCaseReviewForm();
  if (previewSubmitted) {
    return <div ref={statusRef} tabIndex={-1} role="status" className="rounded-lg border-2 border-ink bg-white p-7 md:p-9" data-id="element-53" data-mp="3shxdrphieke0">
        <CheckCircle2Icon aria-hidden="true" className="h-8 w-8 text-ink" data-id="element-54" data-mp="5c6fpdjr7hgq" />
        <h3 className="mt-4 text-h3 font-bold text-ink" data-id="element-55" data-mp="a96lroglsk7u">Preview only — nothing was sent</h3>
        <p className="mt-3 max-w-measure text-slate" data-id="element-56" data-mp="1qp9tih4snk7s">
          This design prototype does not transmit form information. In production, the firm’s approved confirmation
          message appears here.
        </p>
        <button type="button" onClick={reset} className={actionClass("outline", "mt-6")} data-id="element-57" data-mp="1hp4tis1xjo3n">
          Reset the preview form
        </button>
      </div>;
  }
  return <form noValidate onSubmit={handleSubmit} aria-labelledby="case-review-form-title" className="rounded-lg border border-line bg-white p-6 md:p-9" data-id="element-58" data-mp="2achi17rni5zl">
      <h3 id="case-review-form-title" className="text-h3 font-bold text-ink" data-id="element-59" data-mp="25g0kjmykaf9u">
        Request a free case review
      </h3>
      <p className="mt-2 text-meta text-slate" data-id="element-60" data-mp="1vocwlw17rgd4">All fields are required unless marked optional.</p>

      {errorList.length > 0 && <div ref={summaryRef} tabIndex={-1} role="alert" aria-labelledby="form-error-title" className="mt-6 rounded-md border-2 border-crimson bg-white p-5" data-id="element-61" data-mp="2t40jvb9xve01">
          <h4 id="form-error-title" className="text-label font-bold text-crimson" data-id="element-62" data-mp="mfrs7oplle3f">
            Please fix {errorList.length === 1 ? "this item" : `these ${errorList.length} items`}:
          </h4>
          <ul className="mt-2 list-disc space-y-1 pl-6 text-meta" data-id="element-63" data-mp="cowwltqgp25b">
            {errorList.map(item => <li key={item.name} data-id="element-64" data-mp="hm5dlqzvj04z">
                <a href={`#cr-${item.name}`} className="font-semibold text-ink underline underline-offset-4" data-id="element-65" data-mp="2qhfmjl5f99j">
                  {item.message}
                </a>
              </li>)}
          </ul>
        </div>}

      <div className="mt-6 grid gap-6 sm:grid-cols-2" data-id="element-66" data-mp="3px09a6ci49x3">
        <FormField id="cr-firstName" label="First name" autoComplete="given-name" value={values.firstName} onChange={handleChange("firstName")} error={errors.firstName} data-id="element-67" data-mp="3uurxapa0zbw0" />
        <FormField id="cr-lastName" label="Last name" autoComplete="family-name" value={values.lastName} onChange={handleChange("lastName")} error={errors.lastName} data-id="element-68" data-mp="1t2di3xyv8lja" />
        <FormField id="cr-phone" label="Phone number" type="tel" inputMode="tel" autoComplete="tel" value={values.phone} onChange={handleChange("phone")} error={errors.phone} data-id="element-69" data-mp="1y660pnxiwpkf" />
        <FormField id="cr-email" label="Email" type="email" autoComplete="email" value={values.email} onChange={handleChange("email")} error={errors.email} data-id="element-70" data-mp="2lwjtxhrugugl" />
      </div>

      <div className="mt-6 border-t border-line pt-5" data-id="element-71" data-mp="2qwy3j493gcgq">
        <button type="button" aria-expanded={showOptional} aria-controls="cr-optional" onClick={() => setShowOptional(!showOptional)} className="inline-flex min-h-[48px] items-center gap-3 text-label font-bold text-ink hover:text-crimson" data-id="element-72" data-mp="2qy95qzmw5c5q">
          <span className="flex h-7 w-7 items-center justify-center rounded border-2 border-ink" data-id="element-73" data-mp="xzaporzcnwb5">
            {showOptional ? <MinusIcon aria-hidden="true" className="h-4 w-4" data-id="element-74" data-mp="130zzy5er05y6" /> : <PlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-75" data-mp="171jud17xsvgm" />}
          </span>
          Add ZIP code or crash details (optional)
        </button>
        {showOptional && <div id="cr-optional" className="mt-4 grid gap-6" data-id="element-76" data-mp="fb5p1tdzorgk">
            <div className="sm:max-w-[14rem]" data-id="element-77" data-mp="jao5abuv8ed2">
              <FormField id="cr-zip" label="ZIP code" optional inputMode="numeric" autoComplete="postal-code" value={values.zip} onChange={handleChange("zip")} error={errors.zip} data-id="element-78" data-mp="jbfw79dipnx0" />
            </div>
            <FormField id="cr-details" label="Crash details" optional multiline hint="A few sentences is enough." value={values.details} onChange={handleChange("details")} error={errors.details} data-id="element-79" data-mp="ketqbd6va8p" />
          </div>}
      </div>

      <div className="mt-6 rounded-md bg-ivory p-4 text-meta text-slate" data-id="element-80" data-mp="3f6kh3zlfio51">
        <p data-id="element-81" data-mp="3b60gun5k74eo">
          <strong className="text-ink" data-id="element-82" data-mp="3b68tqf1om9pv">Privacy:</strong> Share only what you’re comfortable sending online. Prefer to
          talk instead?{" "}
          <a href={firm.phoneHref} className="font-semibold text-ink underline underline-offset-4" data-id="element-83" data-mp="31h8tksbg18a3">
            Call {firm.phoneDisplay}
          </a>
          .
        </p>
      </div>

      <button type="submit" className={actionClass("call", "mt-6 w-full")} data-id="element-84" data-mp="2rit38rk8q31n">
        Request a case review
      </button>
    </form>;
}